import type { CompanyCreateDto, CompanyPatchDto, CompanySelectDto } from '@@types/api.types.ts';
import { ApiErrorWrapper } from '@hooks/api.hook';
import { errorToast } from '@hooks/toast.hook.tsx';
import type { CompanyFormData } from '@schemas/company.schema';
import { useCallback } from 'react';

export type CompanySubmitMode = 'create' | 'edit';

export type PrepareCompanySubmissionResult =
  | {
      success: false;
    }
  | {
      data: CompanyCreateDto;
      success: true;
    };

type UseCompanyProfileModalSubmitParams = {
  applyCompanyLogoDraft: () => Promise<void>;
  createCompanyProfile: (payload: CompanyCreateDto) => Promise<CompanySelectDto>;
  prepareSubmission: (value: CompanyFormData) => PrepareCompanySubmissionResult;
  updateCompanyProfile: (payload: CompanyPatchDto) => Promise<CompanySelectDto>;
};

const getCompanySubmitErrorMessage = (params: {
  error: unknown;
  fallbackMessage: string;
}): string => {
  const { error, fallbackMessage } = params;

  if (error instanceof ApiErrorWrapper) {
    return error.errors?.[0]?.message ?? error.message;
  }

  if (error instanceof Error) {
    return error.message;
  }

  return fallbackMessage;
};

export const useCompanyProfileModalSubmit = (params: UseCompanyProfileModalSubmitParams) => {
  const { applyCompanyLogoDraft, createCompanyProfile, prepareSubmission, updateCompanyProfile } = params;

  const submitCompanyWithLogo = useCallback(
    async (submitParams: {
      formData: CompanyFormData;
      mode: CompanySubmitMode;
    }): Promise<CompanySelectDto | undefined> => {
      const { formData, mode } = submitParams;
      const submissionResult = prepareSubmission(formData);

      if (!submissionResult.success) {
        errorToast({ description: 'Please fill in all required fields correctly.' });
        return undefined;
      }

      const logoFailureMessage =
        mode === 'edit'
          ? 'Organization updated, but logo update failed.'
          : 'Organization created, but logo upload failed.';
      const submitFailureMessage =
        mode === 'edit' ? 'Failed to update organization.' : 'Failed to create organization.';

      try {
        const companyResponse =
          mode === 'edit'
            ? await updateCompanyProfile(submissionResult.data)
            : await createCompanyProfile(submissionResult.data);

        try {
          await applyCompanyLogoDraft();
        } catch {
          errorToast({ description: logoFailureMessage });
        }

        return companyResponse;
      } catch (error) {
        errorToast({
          description: getCompanySubmitErrorMessage({
            error,
            fallbackMessage: submitFailureMessage,
          }),
        });
      }

      return undefined;
    },
    [applyCompanyLogoDraft, createCompanyProfile, prepareSubmission, updateCompanyProfile],
  );

  return {
    submitCompanyWithLogo,
  };
};
